import type { ClaimCharges } from '../../../../types/LegalCase';

// Helpers puros dos encargos da pretensão (Task 17 / ADR 004). O backend já entrega tudo
// calculado e formatado — aqui ficam só as decisões de **apresentação** e a simulação de
// negociação, testáveis fora de qualquer componente.
//
// Nenhuma destas funções recalcula juros ou multa. O cálculo tem um dono (o domínio do
// `recovery`), e duplicá-lo aqui criaria duas fontes para o mesmo número — que divergiriam no
// primeiro ajuste de regra.

// O bloco de encargos tem algo a mostrar quando há saldo, encargo calculado ou custas.
// Type guard: ao passar no if, o TS estreita para não-nulo e o componente lê sem assertion.
export const hasChargesData = (
    charges: ClaimCharges | null | undefined,
): charges is ClaimCharges => {
    if (!charges) return false;
    return (
        charges.outstandingAmount > 0 ||
        charges.interestAmount !== null ||
        charges.penaltyAmount !== null ||
        charges.legalCosts > 0
    );
};

// Há taxa cadastrada no caso? Sem taxa o backend devolve juros/multa nulos, e a tela precisa
// dizer "cadastre a taxa" em vez de mostrar um valor atualizado igual ao saldo sem explicação.
export const hasChargeRates = (charges: ClaimCharges): boolean =>
    charges.interestAmount !== null || charges.penaltyAmount !== null;

// Algum encargo foi ajustado à mão (override)? A tela sinaliza para o desvio ficar visível.
export const isChargesOverridden = (charges: ClaimCharges): boolean =>
    charges.interestOverridden || charges.penaltyOverridden;

// Quanto os encargos representam sobre o saldo — responde "o acessório já passou o principal?".
// `null` quando não há saldo (percentual sobre zero não existe).
export const chargesRatio = (charges: ClaimCharges): number | null => {
    if (charges.outstandingAmount <= 0) return null;
    return (charges.chargesTotal / charges.outstandingAmount) * 100;
};

// Cor por faixa do peso dos encargos: a partir de 50% âmbar, 100% (dobrou a dívida) vermelho.
export const chargesRatioClass = (ratio: number | null): string => {
    if (ratio === null) return 'text-gray-600 dark:text-gray-400';
    if (ratio >= 100) return 'text-red-600 dark:text-red-400';
    if (ratio >= 50) return 'text-amber-600 dark:text-amber-400';
    return 'text-gray-700 dark:text-gray-300';
};

// ─── Simulação de negociação (ADR 004) ───────────────────────────────────────
// "Receber 50% é melhor do que receber 0%". O **resultado** do acordo já tem casa desde a Task 9
// (`LegalAgreement.agreedAmount`); o que segue é o apoio à decisão: o teto, o desconto e o que
// aquele desconto implica de perda esperada.

export interface DiscountSimulation {
    // Teto da negociação: quanto a dívida vale hoje.
    updatedAmount: number;
    // O que o jurídico propõe aceitar.
    proposedAmount: number;
    discountAmount: number;
    discountPercent: number;
    // Percentual de perda sugerido para o CPC 25 — limitado a 0–100, que é o domínio do campo.
    suggestedLossPercent: number;
}

// Simula um desconto sobre o valor atualizado. `null` quando não dá para comparar (sem valor
// atualizado, ou ele é zero — não existe percentual de desconto sobre zero).
//
// Proposta **acima** do atualizado devolve desconto negativo de propósito: é informação real (o
// jurídico pediu mais do que a conta dá), não erro a esconder. Só a **sugestão de perda** é
// limitada a 0–100, porque ali o destino é um campo com domínio fechado.
export const simulateDiscount = (
    updatedAmount: number | null | undefined,
    proposedAmount: number | null | undefined,
): DiscountSimulation | null => {
    if (updatedAmount === null || updatedAmount === undefined) return null;
    if (proposedAmount === null || proposedAmount === undefined) return null;
    if (updatedAmount <= 0) return null;

    const discountAmount = updatedAmount - proposedAmount;
    const discountPercent = (discountAmount / updatedAmount) * 100;

    return {
        updatedAmount,
        proposedAmount,
        discountAmount,
        discountPercent,
        suggestedLossPercent: clampPercent(discountPercent),
    };
};

// Converte um percentual de desconto no valor proposto — o caminho inverso, para o usuário poder
// digitar "aceito 60%" em vez de calcular o valor de cabeça.
export const proposedFromDiscountPercent = (
    updatedAmount: number | null | undefined,
    discountPercent: number | null | undefined,
): number | null => {
    if (!updatedAmount || updatedAmount <= 0) return null;
    if (discountPercent === null || discountPercent === undefined) return null;
    return updatedAmount * (1 - discountPercent / 100);
};

const clampPercent = (value: number): number => {
    if (value < 0) return 0;
    if (value > 100) return 100;
    return value;
};
